import { useQuery } from '@tanstack/react-query';
import { useRef, useEffect, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import clsx from 'clsx';

import { useAuthContext } from '~/shared/context/useAuthContext';
import MonitorIcon from '~/shared/assets/icons/monitor.svg';
import StorageIcon from '~/shared/assets/icons/storage.svg';
import CurrentUtilizationIcon from '~/shared/assets/icons/current-utilization.svg';
import AutoscalingIcon from '~/shared/assets/icons/auto-scale.svg';
import { VolumeCountResponse, GraphResponse, VolumesCapacityResponse } from '~/shared/types/api';
import { useXFetch } from '~/shared/utils/useXFetch';
import Typography from '~/shared/components/Typography';
import { formatGiB } from '~/shared/utils/formatNumber';
import {
  DEFAULT_REFETCH_INTERVAL_MS,
  UTILIZATION_TRAILING_LAG_MS,
} from '~/shared/constants/polling';

interface StorageSummaryCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subText: string;
  bottomLeftContent?: React.ReactNode;
  bottomRightContent?: React.ReactNode;
}

const baseUrl = `/api/v1/volumes`;

const FILTER_CONFIGS = {
  MONITORED: JSON.stringify({
    field: 'monitoring',
    value:
      'monitored,managed,growing,shrinking,merging,migrating,detaching,snapshotting,modifying,unmigrating',
    op: 'in',
  }),
  AUTOSCALING_ON: JSON.stringify({
    field: 'autoscalingState',
    value: 'on',
    op: 'equals',
  }),
} as const;

export default function KPICards() {
  const api = useXFetch();
  const { activeTenantId } = useAuthContext();

  const utilizationParams = useMemo(() => {
    const nowMs = Date.now();
    const end = new Date(nowMs - UTILIZATION_TRAILING_LAG_MS);
    const start = new Date(end.getTime() - 60 * 1000);
    return new URLSearchParams({
      start_date: start.toISOString(),
      end_date: end.toISOString(),
    }).toString();
  }, []);

  const apiFilters = useMemo(
    () => ({
      monitored: new URLSearchParams({ filters: FILTER_CONFIGS.MONITORED }).toString(),
      autoscalingOn: new URLSearchParams({ filters: FILTER_CONFIGS.AUTOSCALING_ON }).toString(),
    }),
    []
  );

  const { data: utilizationData, isLoading: isLoadingUtilization } = useQuery<GraphResponse>({
    queryKey: ['currentUtilizationAvg'],
    queryFn: () => api<GraphResponse>(`${baseUrl}/utilization-graph?${utilizationParams}`),
    refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
  });

  const { data: capacityData, isLoading: isLoadingCapacity } = useQuery<VolumesCapacityResponse>({
    queryKey: ['volumesCapacity', {}],
    queryFn: () => api<VolumesCapacityResponse>(`${baseUrl}/capacity`),
    refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
  });

  const { data: totalVolumeData, isLoading: isLoadingTotalCount } = useQuery<VolumeCountResponse>({
    queryKey: ['totalVolumeCount', {}],
    queryFn: () => api<VolumeCountResponse>(`${baseUrl}/count`),
    refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
  });

  const { data: monitoredVolumeData, isLoading: isLoadingMonitoredCount } =
    useQuery<VolumeCountResponse>({
      queryKey: ['monitoredVolumeCount', FILTER_CONFIGS.MONITORED],
      queryFn: () => api<VolumeCountResponse>(`${baseUrl}/count?${apiFilters.monitored}`),
      refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
    });

  const { data: autoscalingVolumeData, isLoading: isLoadingAutoscalingCount } =
    useQuery<VolumeCountResponse>({
      queryKey: ['autoscalingVolumeCount', FILTER_CONFIGS.AUTOSCALING_ON],
      queryFn: () => api<VolumeCountResponse>(`${baseUrl}/count?${apiFilters.autoscalingOn}`),
      refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
    });

  const { data: monitoredCapacityData, isLoading: isLoadingMonitoredCapacity } =
    useQuery<VolumesCapacityResponse>({
      queryKey: ['monitoredCapacity', FILTER_CONFIGS.MONITORED],
      queryFn: () => api<VolumesCapacityResponse>(`${baseUrl}/capacity?${apiFilters.monitored}`),
      refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
    });

  const { data: autoscalingCapacityData, isLoading: isLoadingAutoscalingCapacity } =
    useQuery<VolumesCapacityResponse>({
      queryKey: ['autoscalingCapacity', FILTER_CONFIGS.AUTOSCALING_ON],
      queryFn: () =>
        api<VolumesCapacityResponse>(`${baseUrl}/capacity?${apiFilters.autoscalingOn}`),
      refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
    });

  const loadingRefs = useRef({
    total: false,
    monitored: false,
    autoscaling: false,
    utilization: false,
  });

  useEffect(() => {
    Object.keys(loadingRefs.current).forEach((key) => {
      (loadingRefs.current as Record<string, boolean>)[key] = false;
    });
  }, [activeTenantId]);

  const computedValues = useMemo(() => {
    const hasTotalCapacity = typeof capacityData?.capacity === 'number';
    const hasTotalCount = typeof totalVolumeData?.count === 'number';
    const hasMonitoredCapacity = typeof monitoredCapacityData?.capacity === 'number';
    const hasMonitoredCount = typeof monitoredVolumeData?.count === 'number';
    const hasAutoscalingCount = typeof autoscalingVolumeData?.count === 'number';
    const hasAutoscalingCurrent = typeof autoscalingCapacityData?.datafyCapacity === 'number';
    const hasAutoscalingOriginal = typeof autoscalingCapacityData?.capacity === 'number';

    return {
      totalDetectedSize: hasTotalCapacity ? `${formatGiB(capacityData.capacity)}` : undefined,
      totalDetectedCount: hasTotalCount ? `${totalVolumeData.count} volumes` : undefined,
      monitoredStorageSize: hasMonitoredCapacity
        ? `${formatGiB(monitoredCapacityData.capacity)}`
        : undefined,
      monitoredStorageCount: hasMonitoredCount ? `${monitoredVolumeData.count} volumes` : undefined,
      autoscalingStorageCount: hasAutoscalingCount
        ? `${autoscalingVolumeData.count} volumes`
        : undefined,
      autoscalingCurrentSize: hasAutoscalingCurrent
        ? `${formatGiB(autoscalingCapacityData.datafyCapacity)}`
        : undefined,
      autoscalingOriginalSize: hasAutoscalingOriginal
        ? `${formatGiB(autoscalingCapacityData.capacity)}`
        : undefined,
      currentUtilizationValue:
        typeof utilizationData?.avgUtilization === 'number'
          ? `${utilizationData.avgUtilization.toFixed(0)}%`
          : 'N/A',
      // Data availability flags
      hasTotalCapacity,
      hasTotalCount,
      hasMonitoredCapacity,
      hasMonitoredCount,
      hasAutoscalingCount,
      hasAutoscalingCurrent,
      hasAutoscalingOriginal,
    };
  }, [
    capacityData,
    totalVolumeData,
    monitoredCapacityData,
    monitoredVolumeData,
    autoscalingVolumeData,
    autoscalingCapacityData,
    utilizationData,
  ]);

  // Update loading refs when data becomes available
  if (
    !loadingRefs.current.total &&
    computedValues.hasTotalCapacity &&
    computedValues.hasTotalCount
  ) {
    loadingRefs.current.total = true;
  }
  if (
    !loadingRefs.current.monitored &&
    computedValues.hasMonitoredCapacity &&
    computedValues.hasMonitoredCount
  ) {
    loadingRefs.current.monitored = true;
  }
  if (
    !loadingRefs.current.autoscaling &&
    computedValues.hasAutoscalingCount &&
    computedValues.hasAutoscalingCurrent &&
    computedValues.hasAutoscalingOriginal
  ) {
    loadingRefs.current.autoscaling = true;
  }
  if (!loadingRefs.current.utilization && typeof utilizationData?.avgUtilization === 'number') {
    loadingRefs.current.utilization = true;
  }

  const skeletonFlags = {
    showTotalCapacitySkeleton: !loadingRefs.current.total && isLoadingCapacity,
    showTotalCountSkeleton: !loadingRefs.current.total && isLoadingTotalCount,
    showMonitoredCapacitySkeleton: !loadingRefs.current.monitored && isLoadingMonitoredCapacity,
    showMonitoredCountSkeleton: !loadingRefs.current.monitored && isLoadingMonitoredCount,
    showAutoscalingCapacitySkeleton:
      !loadingRefs.current.autoscaling && isLoadingAutoscalingCapacity,
    showAutoscalingCountSkeleton: !loadingRefs.current.autoscaling && isLoadingAutoscalingCount,
    isLoadingCurrentUtilizationValue: !loadingRefs.current.utilization && isLoadingUtilization,
  };

  return (
    <div className="w-full grid grid-cols-5 2xl:flex 3xl:grid 5xl:grid-cols-4 gap-[10px] items-start">
      <StorageSummaryCard
        icon={StorageIcon}
        title="Total storage detected"
        subText="All volumes in account"
        bottomLeftContent={
          <>
            {skeletonFlags.showTotalCapacitySkeleton ? (
              <SkeletonLoader width={80} />
            ) : (
              <Typography variant="H3-resp" fontWeight={500} color="text-primary-900" className="">
                {computedValues.totalDetectedSize}
              </Typography>
            )}
          </>
        }
        bottomRightContent={
          <>
            {skeletonFlags.showTotalCountSkeleton ? (
              <SkeletonLoader width={80} />
            ) : (
              <div className={clsx('bg-gray-200 px-2 py-1 rounded')}>
                <Typography variant="p3-reg" color="text-primary-900">
                  {computedValues.totalDetectedCount}
                </Typography>
              </div>
            )}
          </>
        }
      />
      <StorageSummaryCard
        icon={MonitorIcon}
        title="Monitored storage"
        subText={'Volumes monitored by Datafy'}
        bottomLeftContent={
          <>
            {skeletonFlags.showMonitoredCapacitySkeleton ? (
              <SkeletonLoader width={90} />
            ) : (
              <Typography variant="H3-resp" fontWeight={500} color="text-primary-900" className="">
                {computedValues.monitoredStorageSize}
              </Typography>
            )}
          </>
        }
        bottomRightContent={
          <>
            {skeletonFlags.showMonitoredCountSkeleton ? (
              <SkeletonLoader width={80} />
            ) : (
              <div className={clsx('bg-gray-200 px-2 py-1 rounded')}>
                <Typography variant="p3-reg" color="text-primary-900">
                  {computedValues.monitoredStorageCount}
                </Typography>
              </div>
            )}
          </>
        }
      />
      <div className="col-span-2 basis-2/5 5xl:col-span-1">
        <StorageSummaryCard
          icon={AutoscalingIcon}
          title="Autoscaling storage"
          subText="Volumes managed by Datafy"
          bottomLeftContent={
            <>
              {skeletonFlags.showAutoscalingCapacitySkeleton ? (
                <div className="flex items-center gap-3">
                  <SkeletonLoader width={120} />
                  <div className="h-[24px] 3xl:h-[27px] w-[1px] border border-gray-300" />
                  <SkeletonLoader width={120} />
                </div>
              ) : (
                <div className="flex itemscenter gap-1">
                  <Typography
                    variant="H3-resp"
                    fontWeight={500}
                    color="text-primary-900"
                    className="whitespace-nowrap"
                  >
                    {computedValues.autoscalingCurrentSize ?? 'N/A'}
                  </Typography>
                  <p className="text-gray-500 font-[400] text-sm 2xl:text-lg whitespace-nowrap mt-auto te bg-re">
                    current
                  </p>
                  <div className="ml-2 h-[24px] 3xl:h[30px] w-[1px] border border-gray-300" />
                  <Typography
                    variant="H3-resp"
                    fontWeight={500}
                    color="text-primary-900"
                    className="whitespace-nowrap ml-2"
                  >
                    {computedValues.autoscalingOriginalSize ?? 'N/A'}
                  </Typography>
                  <span className="text-gray-500 font-[400] text-sm 2xl:text-lg whitespace-nowrap mt-auto bg-gree">
                    original
                  </span>
                </div>
              )}
            </>
          }
          bottomRightContent={
            <div className="ml-5">
              {skeletonFlags.showAutoscalingCountSkeleton ? (
                <SkeletonLoader width={80} />
              ) : (
                <div className={clsx('bg-gray-200 px-2 py-1 rounded')}>
                  <Typography
                    variant="p3-reg"
                    color="text-primary-900"
                    className="whitespace-nowrap"
                  >
                    {computedValues.autoscalingStorageCount}
                  </Typography>
                </div>
              )}
            </div>
          }
        />
      </div>
      <StorageSummaryCard
        icon={CurrentUtilizationIcon}
        title="Current Utilization"
        subText="Of monitored volumes"
        bottomLeftContent={
          <>
            {skeletonFlags.isLoadingCurrentUtilizationValue ? (
              <SkeletonLoader width={90} />
            ) : (
              <Typography
                variant="H3-resp"
                fontWeight={500}
                color="text-primary-900"
                className="leading-[28px]!"
              >
                {computedValues.currentUtilizationValue}
              </Typography>
            )}
          </>
        }
      />
    </div>
  );
}

function StorageSummaryCard({
  icon: Icon,
  title,
  subText,
  bottomLeftContent,
  bottomRightContent,
}: StorageSummaryCardProps) {
  return (
    <div className="inline-flex p-[12px] 3xl:p-[20px] 5xl:p-[24px] flex-col justify-between gap-2 2xl:gap-3 5xl:gap-[18px] w-full  shadow-card rounded-[12px] border border-primary-100">
      <div className="flex items-center gap-2  whitespace-nowrap text-ellipsis overflow-hidden">
        <div className="size-9">{Icon && <Icon className="size-9!" />}</div>
        <div className="flex flex-col ">
          <>
            <Typography
              variant="p3"
              fontWeight={500}
              color="text-primary-900 whitespace-nowrap truncate"
              title={title}
            >
              {title}
            </Typography>
            <Typography
              variant="caption"
              color="text-gray-700 font-family-inter whitespace-nowrap truncate w-full "
              title={subText}
            >
              {subText}
            </Typography>
          </>
        </div>
      </div>
      <div className="flex gap-3 items-center justify-between font-family-outfit">
        {bottomLeftContent && <div>{bottomLeftContent}</div>}
        {bottomRightContent && <div className="ml-auto">{bottomRightContent}</div>}
      </div>
    </div>
  );
}

function SkeletonLoader({ width, className }: { width: number; className?: string }) {
  return (
    <Skeleton
      containerClassName=" block leading-[unset!important]"
      className={clsx('h-[27px] 2xl:h[30px] leading-[unset!important] rounded-[8px] ', className)}
      baseColor="var(--color-gray-200)"
      highlightColor="#FFF"
      width={width}
    />
  );
}
